import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import '../styles/views/project-detail.css';

export default function ProjectDetail({ slug, preview = false, onNavigate }) {
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [failedGalleryImageKey, setFailedGalleryImageKey] = useState('');
  const galleryTouchStartX = useRef(null);

  useEffect(() => {
    setGalleryIndex(0);
  }, [slug]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['project', slug, preview],
    queryFn: async () => {
      const res = await fetch(`/api/projects/${slug}${preview ? '?preview=1' : ''}`);
      if (!res.ok) throw new Error('Project not found');
      return res.json();
    }
  });

  const { data: allProjectsData } = useQuery({
    queryKey: ['projects', 'published'],
    queryFn: async () => {
      const res = await fetch('/api/projects');
      return res.json();
    }
  });

  if (isLoading) return <div className="page-state page-state--compact">Carregando projeto...</div>;
  if (error || !data?.project) return (
    <div className="page-state">
      <h2 className="font-display page-state__title page-state__title--sm">Projeto não encontrado</h2>
      <button onClick={() => onNavigate('/projects')} className="btn btn--solid page-state__action">
        Voltar ao Catálogo
      </button>
    </div>
  );

  const proj = data.project;
  let gallery = [];
  let technicalSheet = [];
  try {
    const parsedGallery = JSON.parse(proj.gallery_json || '[]');
    gallery = Array.isArray(parsedGallery) ? parsedGallery.map((item) => (
      typeof item === 'string' ? { url: item, originalUrl: item, mobileUrl: '', caption: '' } : {
        url: item.url || item.src || '',
        originalUrl: item.originalUrl || item.original_url || item.url || item.src || '',
        mobileUrl: item.mobileUrl || item.mobile_url || '',
        caption: item.caption || item.legenda || '',
      }
    )).filter((item) => item.url) : [];
  } catch { /* keep the gallery empty when stored data is invalid */ }
  try {
    const parsedSheet = JSON.parse(proj.technical_sheet_json || '[]');
    technicalSheet = Array.isArray(parsedSheet) ? parsedSheet.filter((row) => row.label && row.value) : [];
  } catch { /* projects created before the technical sheet was added */ }
  const projectsList = allProjectsData?.projects || [];
  const galleryHasCaptions = gallery.some((image) => String(image.caption || '').trim());
  const activeGalleryIndex = Math.min(galleryIndex, Math.max(0, gallery.length - 1));
  const activeGalleryImage = gallery[activeGalleryIndex];
  const activeGalleryImageKey = `${activeGalleryImage?.url || ''}:${activeGalleryIndex}`;
  const useOriginalGalleryImage = failedGalleryImageKey === activeGalleryImageKey;
  const activeGalleryImageUrl = useOriginalGalleryImage
    ? activeGalleryImage?.originalUrl || activeGalleryImage?.url
    : activeGalleryImage?.url;
  const handleGalleryPointerDown = (event) => {
    if (event.pointerType !== 'touch') return;
    galleryTouchStartX.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handleGalleryPointerUp = (event) => {
    if (galleryTouchStartX.current === null) return;
    const swipeDistance = event.clientX - galleryTouchStartX.current;
    galleryTouchStartX.current = null;
    if (Math.abs(swipeDistance) < 50 || gallery.length < 2) return;
    setGalleryIndex((currentIndex) => (
      currentIndex + (swipeDistance < 0 ? 1 : -1) + gallery.length
    ) % gallery.length);
  };
  const handleGalleryPointerCancel = () => {
    galleryTouchStartX.current = null;
  };
  const currentIndex = projectsList.findIndex(p => p.slug === slug);
  const prevProject = currentIndex > 0 ? projectsList[currentIndex - 1] : null;
  const nextProject = currentIndex >= 0 && currentIndex < projectsList.length - 1 ? projectsList[currentIndex + 1] : null;

  return (
    <div className="project-detail">
      {/* Breadcrumbs */}
      <div className="container project-detail__crumbs">
        <div className="font-label breadcrumbs">
          <a href="/" onClick={(e) => { e.preventDefault(); onNavigate('/'); }} className="breadcrumbs__link">Início</a> /
          <a href="/projects" onClick={(e) => { e.preventDefault(); onNavigate('/projects'); }} className="breadcrumbs__link">Projetos</a> /
          <span>{proj.title}</span>
        </div>
      </div>

      {/* Project Header */}
      <div className="container project-detail__header">
        <div className="project-detail__intro">
          <p className="font-label project-detail__meta">
            {(proj.sector === 'museum' || proj.sector === 'expography' ? 'EXPOGRAFIA' : proj.sector.toUpperCase())} • <span className="project-detail__meta-year">{proj.city ? `${proj.city} • ` : ''}{proj.year || new Date(proj.created_at).getFullYear()}</span>
          </p>
          <h1 className="font-display project-detail__title">
            {proj.title}
          </h1>
          <p className="project-detail__summary">
            {proj.summary}
          </p>
        </div>
      </div>

      {/* Cover Image */}
      <div className="project-detail__cover-shell">
        <figure className="project-detail__cover">
          <img className="project-detail__cover-img" src={proj.cover_image} alt={proj.title} />
          {proj.cover_caption && <figcaption className="font-label project-detail__cover-caption">{proj.cover_caption}</figcaption>}
        </figure>
      </div>

      <div className="container">
        <div className="project-detail__layout">
          {/* Main Body */}
          <div className="project-detail__body">
            <div className="project-detail__story">
              {proj.body}
            </div>
          </div>

          {/* Technical Sheet Side */}
          <div className="project-detail__aside">
            <dl className="tech-sheet">
              {technicalSheet.map((row, index) => (
                <React.Fragment key={`${row.label}-${index}`}>
                  <dt className="font-label tech-sheet__label">{row.label}</dt>
                  <dd className="tech-sheet__value">{row.value}</dd>
                </React.Fragment>
              ))}
              {proj.city && <><dt className="font-label tech-sheet__label">Cidade</dt><dd className="tech-sheet__value">{proj.city}</dd></>}
              <dt className="font-label tech-sheet__label">Ano</dt>
              <dd className="tech-sheet__value">{proj.year || new Date(proj.created_at).getFullYear()}</dd>
              <dt className="font-label tech-sheet__label">Tipo</dt>
              <dd className="tech-sheet__value">{proj.sector === 'museum' || proj.sector === 'expography' ? 'Expografia' : proj.sector === 'residential' ? 'Residencial' : 'Comercial'}</dd>
            </dl>
            {proj.video_url && <a className="project-detail__video-link" href={proj.video_url} target="_blank" rel="noreferrer">Assista ao vídeo</a>}
          </div>
        </div>
      </div>

      {/* Additional images carousel */}
      {gallery.length > 0 && (
        <div className="project-gallery" role="region" aria-roledescription="carousel" aria-label={`Imagens adicionais de ${proj.title}`}>
            <figure className="project-gallery__item" aria-roledescription="slide" aria-label={`Imagem ${activeGalleryIndex + 1} de ${gallery.length}`}>
              <div
                className="project-gallery__stage"
                onPointerDown={handleGalleryPointerDown}
                onPointerUp={handleGalleryPointerUp}
                onPointerCancel={handleGalleryPointerCancel}
              >
                <picture>
                  {!useOriginalGalleryImage && activeGalleryImage.mobileUrl && (
                    <source media="(max-width: 560px)" srcSet={activeGalleryImage.mobileUrl} />
                  )}
                  <img
                    src={activeGalleryImageUrl}
                    alt={activeGalleryImage.caption || `${proj.title} — imagem ${activeGalleryIndex + 1}`}
                    className="project-gallery__img"
                    draggable="false"
                    onError={() => {
                      if (!useOriginalGalleryImage && activeGalleryImage.originalUrl !== activeGalleryImage.url) {
                        setFailedGalleryImageKey(activeGalleryImageKey);
                      }
                    }}
                  />
                </picture>
              </div>
              {galleryHasCaptions && (
                <figcaption className="font-label project-gallery__caption" aria-hidden={!activeGalleryImage.caption || undefined}>
                  {String(activeGalleryImage.caption || '').trim() || '\u00a0'}
                </figcaption>
              )}
            </figure>

            {gallery.length > 1 && (
              <div className="project-gallery__controls">
                <button
                  type="button"
                  className="project-gallery__arrow"
                  aria-label="Ver imagem anterior"
                  onClick={() => setGalleryIndex((activeGalleryIndex - 1 + gallery.length) % gallery.length)}
                >
                  <ArrowLeft size={18} />
                </button>
                <div className="project-gallery__pagination" aria-label="Selecionar imagem">
                  {gallery.map((image, index) => (
                    <button
                      key={`${image.url}-${index}`}
                      type="button"
                      className={`project-gallery__dot ${index === activeGalleryIndex ? 'is-active' : ''}`}
                      aria-label={`Ver imagem ${index + 1}`}
                      aria-current={index === activeGalleryIndex ? 'true' : undefined}
                      onClick={() => setGalleryIndex(index)}
                    />
                  ))}
                </div>
                <span className="font-label project-gallery__count">{String(activeGalleryIndex + 1).padStart(2, '0')} / {String(gallery.length).padStart(2, '0')}</span>
                <button
                  type="button"
                  className="project-gallery__arrow"
                  aria-label="Ver próxima imagem"
                  onClick={() => setGalleryIndex((activeGalleryIndex + 1) % gallery.length)}
                >
                  <ArrowRight size={18} />
                </button>
              </div>
            )}
        </div>
      )}

      {/* Project Pager */}
      <div className="project-pager">
        <div className="container project-pager__inner">
          {prevProject ? (
            <a
              href={`/projects/${prevProject.slug}`}
              onClick={(e) => { e.preventDefault(); onNavigate(`/projects/${prevProject.slug}`); }}
              className="project-pager__link"
            >
              <small className="font-label project-pager__label"><ArrowLeft size={14} /> Projeto anterior</small>
              <strong className="font-display project-pager__title">{prevProject.title}</strong>
            </a>
          ) : <div />}
          {nextProject ? (
            <a
              href={`/projects/${nextProject.slug}`}
              onClick={(e) => { e.preventDefault(); onNavigate(`/projects/${nextProject.slug}`); }}
              className="project-pager__link project-pager__link--next"
            >
              <small className="font-label project-pager__label">Próximo projeto <ArrowRight size={14} /></small>
              <strong className="font-display project-pager__title">{nextProject.title}</strong>
            </a>
          ) : <div />}
        </div>
      </div>
    </div>
  );
}
